import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import SideDrawer from '../SideDrawer';
import {
  DrawerNavigation,
  SideDrawerRef,
} from '../SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useRevokeFileAccess from 'router/subrouters/DataRoom/subrouters/Members/pages/Members/hooks/useRevokeFIleAccess';
import useConfirmRevokeInOrder from 'router/subrouters/DataRoom/hooks/useConfirmRevokeInOrder';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { getUserFullName } from 'utils';
import MemberSettings from './components/MemberSettings';
import MemberActivity from './components/MemberActivity';
import {
  drawerNavigationTabs,
  MEMBER_DRAWER_OPTIONS,
  MemberDrawerOption,
} from './data';

import './DocumentsAvailabilityDrawer.styles.scss';

type DocumentsAvailabilityDrawerProps = {
  className?: string;
  member: DataRoomMember;
  isBuySide: boolean;
  defaultOpenRoles?: boolean;
  hasAdditionalActions?: boolean;
  // Which tab the drawer lands on each time it opens. The row menu uses it to
  // send "Edit permissions" to Settings and "View activity" to Activity.
  defaultTab?: MemberDrawerOption;
  updateMember?: (member: DataRoomMember) => void;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
} & DrawerNavigation;

const DocumentsAvailabilityDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DocumentsAvailabilityDrawerProps
> = (
  {
    className,
    member,
    isBuySide,
    hasAdditionalActions,
    defaultOpenRoles = false,
    defaultTab = MEMBER_DRAWER_OPTIONS.SETTINGS,
    updateMember,
    updateDataRoomMembers,
    onPrev,
    onNext,
  },
  ref,
) => {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [selectedTab, setSelectedTab] =
    useState<MemberDrawerOption>(defaultTab);

  const isOpen = drawer?.open;

  // The drawer stays mounted between members, so the tab it was left on has to
  // be reset every time it reopens rather than only on mount.
  useEffect(() => {
    if (isOpen) setSelectedTab(defaultTab);
  }, [defaultTab, isOpen]);

  const classes = classNames('documents-availability-drawer', className);

  const { t } = useTranslation();

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const handleOnRevokeAccess = useCallback(
    () => dataRoomApi.members.revokeAccess(member?._id),
    [member],
  );

  const handleOnUndoRevokeAccess = useCallback(
    () => dataRoomApi.members.undoRevokeAccess(member?._id),
    [member],
  );

  const { confirmRevokeAll } = useConfirmRevokeInOrder();

  const confirmRevokeAccess = useCallback(
    () => confirmRevokeAll(member?.isLead ? [member._id] : []),
    [confirmRevokeAll, member],
  );

  const updateValueOnRevokeSuccess = useCallback(
    (member: DataRoomMember) => {
      updateMember({
        ...member,
        accessableDataRoomNodesView: [],
        accessableDataRoomNodesDownload: [],
        hasFullAccess: false,
        hasFullDownloadAccess: false,
      });
    },
    [updateMember],
  );

  const {
    isLoadingRevoke,
    isUndoAvailable,
    refetchFlag,
    hasFileAccess,
    onRevokeAccess,
    onUndoRevokeAccess,
    refetch,
  } = useRevokeFileAccess({
    value: member,
    numberOfAccess: member?.accessableDataRoomNodesView?.length,
    handleOnRevokeAccess,
    handleOnUndoRevokeAccess,
    confirmRevokeAccess,
    updateValueOnRevokeSuccess,
    updateValueOnUndoSuccess: updateMember,
  });

  const closeDrawer = useCallback(() => drawer?.setOpen(false), [drawer]);

  const navItems = useMemo(() => Object.values(drawerNavigationTabs), []);

  const drawerContent = useMemo(
    () => ({
      [MEMBER_DRAWER_OPTIONS.SETTINGS]: (
        <MemberSettings
          member={member}
          isBuySide={isBuySide}
          drawerOpen={isOpen}
          defaultOpenRoles={defaultOpenRoles}
          hasAdditionalActions={hasAdditionalActions}
          refetchFlag={refetchFlag}
          hasFileAccess={hasFileAccess}
          isLoadingRevoke={isLoadingRevoke}
          isUndoAvailable={isUndoAvailable}
          onRevokeAccess={onRevokeAccess}
          refetch={refetch}
          updateMember={updateMember}
          updateDataRoomMembers={updateDataRoomMembers}
          closeDrawer={closeDrawer}
        />
      ),
      [MEMBER_DRAWER_OPTIONS.ACTIVITY]: (
        <MemberActivity dataRoomId={dataRoomId} dataRoomMember={member} />
      ),
    }),
    [
      member,
      isBuySide,
      dataRoomId,
      isOpen,
      defaultOpenRoles,
      hasAdditionalActions,
      refetchFlag,
      hasFileAccess,
      isLoadingRevoke,
      isUndoAvailable,
      onRevokeAccess,
      refetch,
      updateMember,
      updateDataRoomMembers,
      closeDrawer,
    ],
  );

  return (
    <>
      <SideDrawer
        ref={drawerRef}
        title={
          member?.isAccepted ? (
            <>
              {getUserFullName(member?.firstName, member?.lastName)}
              {!member?.user?.hasVerifiedEmail && (
                <span className="not-verified">
                  {t('notVerifiedInBrackets')}
                </span>
              )}
            </>
          ) : (
            <>
              {t('invited')}
              <span className="email">{member?.email}</span>
            </>
          )
        }
        className={classes}
        onPrev={onPrev}
        onNext={onNext}
      >
        <header>
          <div className="documents-availability-drawer__navbar">
            {navItems.map(({ icon, value }) => (
              <Button
                key={value}
                icon={icon}
                className={classNames({ selected: value === selectedTab })}
                onClick={() => setSelectedTab(value)}
              >
                {t(value)}
              </Button>
            ))}
          </div>
        </header>

        {drawerContent[selectedTab]}
      </SideDrawer>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingRevoke}
        onUndo={onUndoRevokeAccess}
      >
        <p>{t('fileAccessRevokedMember')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(DocumentsAvailabilityDrawer);
